import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import logo from "../../assets/logo.png";
import { companyService } from "../../services/company.service";
import { CompanyDetails } from "../../services/types";

const footerLinks = {
    company: [
        { name: "About Us", path: "/about" },
        { name: "News", path: "/investments" },
        { name: "Blogs", path: "/blogs" },
        { name: "Contact", path: "/contact" },
    ],
    services: [
        { name: "Insurance", path: "/services" },
        { name: "Investments", path: "/services" },
        { name: "Financial Planning", path: "/services" },
        { name: "Wealth Management", path: "/services" },
    ],
};

export default function Footer() {
    const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);

    useEffect(() => {
        const fetchCompanyDetails = async () => {
            try {
                const data = await companyService.getCompanyDetails();
                setCompanyDetails(data);
            } catch (error) {
                console.error("Failed to load footer company details", error);
            }
        };
        fetchCompanyDetails();
    }, []);

    return (
        <footer className="bg-gray-900 text-white">
            <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
                {/* Main Footer Grid */}
                <div className="grid lg:grid-cols-12 gap-12 mb-12">
                    {/* Brand Column */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-4"
                    >
                        <Link
                            to="/"
                            className="inline-flex items-center space-x-3 mb-6"
                        >
                            <img
                                src={logo}
                                alt="Seva Consultancy"
                                className="h-12 w-12 object-contain"
                            />
                            <div>
                                <span className="text-lg font-display font-semibold text-white block">
                                    Seva Consultancy
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.25em] text-[#d4b86a]">
                                    Est. 2008
                                </span>
                            </div>
                        </Link>
                        <p className="text-gray-400 leading-relaxed mb-6 max-w-sm text-sm">
                            Crafting bespoke financial strategies for discerning
                            clients. Your wealth, our expertise, shared vision.
                        </p>

                        {/* Contact Info */}
                        <div className="space-y-3">
                            <a
                                href={`tel:${companyDetails?.mobileNumber || "+91 98259 59058"}`}
                                className="flex items-center space-x-3 text-gray-400 hover:text-[#d4b86a] transition-colors group text-sm"
                            >
                                <span className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#d4b86a]/10 transition-colors">
                                    <Phone className="w-4 h-4 text-[#d4b86a]" />
                                </span>
                                <span>{companyDetails?.mobileNumber || "Loading..."}</span>
                            </a>
                            <a
                                href={`mailto:${companyDetails?.email || "official@sevaconsultancy.co.in"}`}
                                className="flex items-center space-x-3 text-gray-400 hover:text-[#d4b86a] transition-colors group text-sm"
                            >
                                <span className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#d4b86a]/10 transition-colors">
                                    <Mail className="w-4 h-4 text-[#d4b86a]" />
                                </span>
                                <span>{companyDetails?.email || "official@sevaconsultancy.co.in"}</span>
                            </a>
                            <div className="flex items-start space-x-3 text-gray-400 text-sm">
                                <span className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-4 h-4 text-[#d4b86a]" />
                                </span>
                                <span>
                                    {companyDetails?.address || "Loading..."}
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Links Columns */}
                    <div className="lg:col-span-4 grid grid-cols-2 gap-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            <h4 className="text-sm font-semibold text-white mb-4">
                                Company
                            </h4>
                            <ul className="space-y-3">
                                {footerLinks.company.map((link) => (
                                    <li key={link.name}>
                                        <Link
                                            to={link.path}
                                            className="text-gray-400 hover:text-[#d4b86a] transition-colors text-sm"
                                        >
                                            {link.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h4 className="text-sm font-semibold text-white mb-4">
                                Services
                            </h4>
                            <ul className="space-y-3">
                                {footerLinks.services.map((link) => (
                                    <li key={link.name}>
                                        <Link
                                            to={link.path}
                                            className="text-gray-400 hover:text-[#d4b86a] transition-colors text-sm"
                                        >
                                            {link.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>

                    {/* CTA Column */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="lg:col-span-4"
                    >
                        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                            <h4 className="font-display text-xl text-white mb-3">
                                Begin Your Journey
                            </h4>
                            <p className="text-gray-400 text-sm mb-5 leading-relaxed">
                                Schedule a consultation with our senior
                                advisors.
                            </p>
                            <Link to="/contact">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full btn-primary flex items-center justify-center space-x-2"
                                >
                                    <span>Schedule Meeting</span>
                                    <ArrowUpRight className="w-4 h-4" />
                                </motion.button>
                            </Link>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/10 pt-6">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <p className="text-gray-500 text-xs">
                            © {new Date().getFullYear()} Seva Consultancy. All
                            rights reserved.
                        </p>
                        <div className="flex items-center space-x-6 text-xs text-gray-500">
                            <Link
                                to="/contact"
                                className="hover:text-[#d4b86a] transition-colors"
                            >
                                Privacy Policy
                            </Link>
                            <Link
                                to="/contact"
                                className="hover:text-[#d4b86a] transition-colors"
                            >
                                Terms of Service
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
